import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';

/* --------------------------------------------------------------------------
   Facebook connect.

   The Pages are saved by the callback on api.schedpilot.com, server-side, so
   this component does NOT depend on the popup being able to talk back to us.

   That dependency is what broke for a subset of customers: Facebook serves
   `Cross-Origin-Opener-Policy: same-origin-allow-popups` on login.php and
   business.facebook.com, which severs window.opener for a popup opened from a
   different origin. Anyone already signed in to Facebook skips those pages and
   keeps their opener; anyone who has to log in loses it, and the postMessage
   never arrived.

   So there are two independent paths to refreshing the list:
     1. postMessage, when the channel happens to survive — instant.
     2. Noticing the popup has closed — always works.
   -------------------------------------------------------------------------- */

const API_ORIGIN = 'https://api.schedpilot.com';

function FacebookConnect() {
    const { getAccountsData } = useAuth();

    const [connecting, setConnecting] = useState(false);
    const popupRef = useRef(null);
    const pollRef = useRef(null);
    const handledRef = useRef(false);

    // Keep the latest refresh function without making the listener depend on
    // it. The context value is a new object on every provider render, so using
    // it as a dependency churned this effect constantly.
    const refreshRef = useRef(getAccountsData);
    refreshRef.current = getAccountsData;

    const finish = (ok, message) => {
        if (handledRef.current) return;      // whichever path wins, only act once
        handledRef.current = true;

        clearInterval(pollRef.current);
        setConnecting(false);

        if (ok === false) {
            toast.error(message || 'Could not connect your Facebook Pages.');
        } else if (message) {
            toast.success(message);
        }

        refreshRef.current();
    };

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin !== API_ORIGIN) return;
            if (event.data?.type !== 'facebook-authenticated') return;

            // `saved` tells us the server already stored the Pages. Older
            // callbacks sent only a token, so treat a missing flag as success
            // and let the refresh reveal the truth.
            finish(event.data.saved !== false, event.data.message);
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
            clearInterval(pollRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFacebookConnect = () => {
        const clientId = '1316741103140098';
        const redirectUri = `${API_ORIGIN}/wp-json/api/v1/facebook-callback`;
        // business_management is required for Pages owned by a Meta Business
        // portfolio. Without it /me/accounts silently omits them (a change Meta
        // made in Graph API v17), which is why agency customers saw "No
        // Facebook Pages found" no matter how many times they reconnected —
        // verified against app 1316741103140098, where a business-owned Page
        // appears in /me/accounts only when this permission is on the token.
        //
        // It is gated on Advanced Access. Until that review is approved Meta
        // simply does not grant it to users without a role on the app, and
        // login proceeds with the remaining permissions, so shipping this
        // ahead of approval is safe.
        const scope = 'pages_show_list,pages_manage_posts,pages_read_engagement,business_management,public_profile';

        const fbUrl = `https://www.facebook.com/v19.0/dialog/oauth?client_id=${clientId}`
            + `&redirect_uri=${encodeURIComponent(redirectUri)}`
            + `&scope=${scope}&response_type=code&auth_type=rerequest`;

        const width = 600, height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        handledRef.current = false;
        setConnecting(true);

        popupRef.current = window.open(
            fbUrl,
            'FacebookAuth',
            `width=${width},height=${height},top=${top},left=${left},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        if (!popupRef.current) {
            setConnecting(false);
            toast.error('Your browser blocked the popup. Allow popups for this site and try again.');
            return;
        }

        // The reliable path: when the popup goes away the work is already done
        // server-side, so just refresh. This is what rescues every customer
        // whose opener was severed during the Facebook login step.
        clearInterval(pollRef.current);
        pollRef.current = setInterval(() => {
            if (popupRef.current && popupRef.current.closed) {
                finish(true, '');
            }
        }, 700);
    };

    return (
        <button
            className="btn btn-primary"
            onClick={handleFacebookConnect}
            disabled={connecting}
        >
            {connecting ? 'Waiting for Facebook…' : 'Connect Facebook'}
        </button>
    );
}

export { FacebookConnect };
